import React, { useRef, useMemo } from 'react';
import { useFrame, useThree } from '@react-three/fiber';
import { Stars } from '@react-three/drei';
import * as THREE from 'three';
import { shared } from './shared';
import { useStore } from '../store';

const c = (hex) => new THREE.Color(hex);
const COL = {
  dayBg: c('#cfe0d6'), nightBg: c('#05070f'), stormBg: c('#5b4a8c'),
  dayFog: c('#c6d8cd'), nightFog: c('#060912'), stormFog: c('#3a2c5e'),
  daySun: c('#ffe9c2'), nightSun: c('#7f9bd6'), stormSun: c('#a98bff'),
  dayHemi: c('#e2f0e8'), nightHemi: c('#243049'),
  duskBg: c('#f0803a'), duskFog: c('#d16a37'), duskSun: c('#ff9a3c'), duskHemi: c('#c76a4a'),
};

const CYCLE = 150; // seconds for a full 24h cycle

export default function WorldClock() {
  const { scene } = useThree();
  const sun = useRef();
  const hemi = useRef();
  const amb = useRef();
  const sunMesh = useRef();
  const moonMesh = useRef();

  const stormState = useRef({ active: false, until: 0, next: 40 + Math.random() * 30, level: 0 });
  const hudAcc = useRef(0);

  // Ensure fog exists and is mutable
  useMemo(() => {
    scene.fog = new THREE.Fog(COL.nightFog.clone(), 70, 340);
    scene.background = COL.nightBg.clone();
  }, [scene]);

  const tmp = useMemo(() => ({ bg: new THREE.Color(), fog: new THREE.Color(), sun: new THREE.Color(), hemi: new THREE.Color() }), []);

  useFrame((_, delta) => {
    const dt = Math.min(delta, 0.05);
    const t = useStore.getState();

    // Advance time of day (auto-cycle) or follow the manual slider
    if (t.autoCycle) {
      shared.env.dayTime = (shared.env.dayTime + dt / CYCLE) % 1;
    } else {
      shared.env.dayTime = t.timeValue;
    }
    const angle = shared.env.dayTime * Math.PI * 2;
    const sunElev = Math.sin(angle - Math.PI / 2); // -1 midnight, +1 noon
    const day = THREE.MathUtils.clamp((sunElev + 0.18) / 0.5, 0, 1);
    const night = 1 - day;
    shared.env.night = night;

    // Weather-driven storm level — the Rain Intensity slider (0..1) drives density
    const ss = stormState.current;
    const wantStorm = t.rainIntensity > 0.01 || shared.forceStorm || shared.photo === 'aerial';
    const mist = t.weather === 'mist' ? 1 : 0;
    const targetLevel = (shared.forceStorm || shared.photo === 'aerial') ? 1 : t.rainIntensity;
    ss.level = THREE.MathUtils.lerp(ss.level, targetLevel, dt * (targetLevel > ss.level ? 1.3 : 0.9));
    shared.env.storm = ss.level;
    const storm = ss.level;

    // Colors: blend night->day, add a warm sunrise/sunset horizon glow, then storm-purple
    const horizon = 1 - THREE.MathUtils.clamp(Math.abs(sunElev) / 0.28, 0, 1); // 1 when sun sits on the horizon
    const dusk = horizon * (1 - storm);
    tmp.bg.copy(COL.nightBg).lerp(COL.dayBg, day).lerp(COL.duskBg, dusk * 0.62).lerp(COL.stormBg, storm * 0.92);
    tmp.fog.copy(COL.nightFog).lerp(COL.dayFog, day).lerp(COL.duskFog, dusk * 0.55).lerp(COL.stormFog, storm * 0.92);
    tmp.sun.copy(COL.nightSun).lerp(COL.daySun, day).lerp(COL.duskSun, dusk * 0.7).lerp(COL.stormSun, storm);
    tmp.hemi.copy(COL.nightHemi).lerp(COL.dayHemi, day).lerp(COL.duskHemi, dusk * 0.5);

    scene.background.copy(tmp.bg);
    scene.fog.color.copy(tmp.fog);
    scene.fog.near = 70;
    scene.fog.far = (340 - storm * 190) * (mist ? 0.5 : 1);

    // Sun position + intensity
    const sx = Math.cos(angle) * 300;
    const sy = sunElev * 220;
    const sz = 120;
    if (sun.current) {
      sun.current.position.set(sx, Math.max(sy, -40), sz);
      sun.current.intensity = (0.12 + day * 1.5) * (1 - 0.7 * storm);
      sun.current.color.copy(tmp.sun);
    }
    if (hemi.current) {
      hemi.current.intensity = 0.22 + day * 0.85;
      hemi.current.color.copy(tmp.hemi);
    }
    if (amb.current) amb.current.intensity = 0.1 + day * 0.32 + storm * 0.05;

    // Sun & moon discs
    const dirLen = Math.hypot(sx, sy, sz) || 1;
    if (sunMesh.current) {
      sunMesh.current.position.set((sx / dirLen) * 500, (sy / dirLen) * 500, (sz / dirLen) * 500);
      sunMesh.current.visible = sunElev > -0.1;
      sunMesh.current.material.color.copy(tmp.sun);
    }
    if (moonMesh.current) {
      moonMesh.current.position.set((-sx / dirLen) * 500, (-sy / dirLen) * 500, (sz / dirLen) * 500);
      moonMesh.current.visible = sunElev < 0.1;
    }

    // Throttled HUD sync
    hudAcc.current += dt;
    if (hudAcc.current > 0.35) {
      hudAcc.current = 0;
      const dtd = shared.env.dayTime;
      const label = dtd < 0.23 ? 'NIGHT' : dtd < 0.32 ? 'DAWN' : dtd < 0.68 ? 'DAY' : dtd < 0.78 ? 'DUSK' : 'NIGHT';
      t.setEnvHud(label, night > 0.5);
      t.setStormActive(t.rainIntensity > 0.01 || shared.forceStorm);
      if (t.autoCycle) t.setTimeValue(shared.env.dayTime);
    }
  });

  return (
    <group>
      <hemisphereLight ref={hemi} args={['#e2f0e8', '#16281c', 0.9]} />
      <ambientLight ref={amb} intensity={0.35} />
      <directionalLight
        ref={sun}
        position={[120, 180, 120]}
        intensity={1.4}
        color="#ffe9c2"
        castShadow
        shadow-mapSize={[2048, 2048]}
        shadow-camera-left={-260}
        shadow-camera-right={260}
        shadow-camera-top={260}
        shadow-camera-bottom={-260}
        shadow-camera-far={700}
      />
      <mesh ref={sunMesh} position={[300, 300, 120]}>
        <sphereGeometry args={[22, 16, 16]} />
        <meshBasicMaterial color="#ffe9c2" toneMapped={false} fog={false} />
      </mesh>
      <mesh ref={moonMesh} position={[-300, 300, 120]}>
        <sphereGeometry args={[14, 16, 16]} />
        <meshBasicMaterial color="#cdd6f0" toneMapped={false} fog={false} />
      </mesh>
      <Stars radius={480} depth={80} count={2600} factor={7} saturation={0} fade speed={0.6} />
    </group>
  );
}
